import logging from '../logging';
import { Wall } from "../domain";
import { clamp } from '../math/clamp';
import { approx, trunc } from '../measurement';


const log = logging('wallControllers');

/**
 * Computes the maximum depth of all units on the wall. Returns zero for null
 * wall arguments because it's convenient for callers.
 */
export const wallDepth = (wall?: Wall) => {
    let max = 0;
    if(wall) {
        for(const unit of wall.closetUnits) {
            max = Math.max(max, unit.depth);
        }
    }
    return max;
}


/**
 * What's the highest point on the wall?
 */
export const maxHeight = (wall: Wall) => {
    let max = 0;
    for(const point of wall.heights) {
        max = Math.max(max, point.height);
    }
    return max;
}

export const wallLength = (wall: Wall) => {
    const h = wall.heights;
    // return the last height x or 0
    return h[h.length - 1]?.x ?? 0;
}


export const setWallLength = (wall: Wall, length: number) => {
    const h = wall.heights;
    return h[h.length - 1].x = length;
}

/**
 * Users edit the wall's length and direction, but our data representation
 * stores this information somewhat redundantly by also storing the wall's
 * positions. This routine ignores all but the first wall's position and moves
 * each subsequent wall to be connected to the previous, maintaining the length
 * and direction.
 */
export const setPositionsFromLengthAndDirection = (walls: Wall[]) => {
    const
        len = walls.length,
        first = walls[0];

    let
        x = first.position.x,
        z = first.position.z,
        firstLen = wallLength(first),
        firstDir = first.rotation.y;
    x = x + firstLen * Math.cos(firstDir);
    z = z + firstLen * -Math.sin(firstDir);
    for(let i=1; i < len; i++) {
        const
            wall = walls[i],
            wallLen = wallLength(wall),
            wallDir = wall.rotation.y;
        wall.position.x = x;
        wall.position.z = z;
        x = x + wallLen * Math.cos(wallDir);
        z = z + wallLen * -Math.sin(wallDir);
    }
}

/**
 * Very important to note that the connection from a to b is directed, so even 
 * if `isConnected(a, b)` is true, `isConnected(b, a)` may not be true. In the
 * case where both are true, a and b have the same length and direction, and
 * each starts at the endpoint of the other.
 */
export const isConnected = (a: Wall, b: Wall): boolean => {
    const
        r = a.rotation.y,
        t = wallLength(a),
        ax = a.position.x + (t* Math.cos(r)),
        az = a.position.z + (t* -Math.sin(r)),
        bx = b.position.x,
        bz = b.position.z;
    return approx(ax, bx) && approx(az, bz);
}

/**
 * Use the Wall.id and Wall.parentId fields to cluster the walls into
 * contiguous groups.
 */
export const groupWallsById = (walls: Wall[]): Set<Wall[]> => {
    const
        result = new Set<Wall[]>(),
        roots = new Set<Wall>(),
        byParentOrder = new Map<number, Wall>();
    for(const wall of walls) {
        if(wall.parentWallOrder < 1) {
            roots.add(wall);
        } else {
            byParentOrder.set(wall.parentWallOrder, wall);
        }
    }
    for(const root of roots) {
        const group = [root];
        let parentId = root.wallOrder;
        while(byParentOrder.has(parentId)) {
            const child = byParentOrder.get(parentId);
            group.push(child);
            parentId = child.wallOrder;
        }
        result.add(group);
    }

    return result;
}

export const groupWalls = (walls: Wall[]): [Set<Wall>, Map<Wall, Wall>] => {
    const
        roots = new Set(walls),
        links = new Map<Wall, Wall>();
    outer: for(const wall of walls) {
        const
            r = wall.rotation.y,
            len = wallLength(wall),
            x = wall.position.x + (len * Math.cos(r)),
            z = wall.position.z + (len * -Math.sin(r));
        for(const other of walls) {
            const 
                ox = other.position.x,
                oz = other.position.z;
 
            // Avoid creating circular links, which would result in the cycle
            // being removed from the roots set.
            if(approx(x, ox) && approx(z, oz) && !links.has(other))
            {
                links.set(wall, other);
                roots.delete(other);
                continue outer;
            }
        }
    }
    return [roots, links];
}

/**
 * Sort the given walls so that connected walls are contiguous in the list and
 * the walls appear in order according to their direction (each is a directed
 * line segment with origin, direction, and length).
 */
export const sortedByConnection = (walls: Wall[]): Wall[] => {
    const
        [roots, links] = groupWalls(walls),
        result: Wall[] = [];

    let counter = 1;

    for(const root of roots) {
        result.push(root);
        if(root.wallOrder == null) root.wallOrder = counter++;
        let lastId = root.wallOrder;
        let link = links.get(root);
        while(link != undefined) {
            result.push(link);
            link.parentWallOrder = lastId;
            if(link.wallOrder == null) link.wallOrder = counter++;
            lastId = link.wallOrder
            link = links.get(link);
        }
    }

    return result;
}

/**
 * Group the walls into sub-arrays of connected walls. Islands will result in
 * a group of one or two walls for single and double sided respectively.
 */
export const connectedWalls = (walls: Wall[]): Wall[][] => {
    const
        [roots, links] = groupWalls(walls),
        result: Wall[][] = [];

    let counter = 1;

    for(const root of roots) {
        const group: Wall[] = [];
        group.push(root);
        if(root.wallOrder == null) root.wallOrder = counter++;
        let lastId = root.wallOrder;
        let link = links.get(root);
        while(link != undefined) {
            group.push(link);
            link.parentWallOrder = lastId;
            if(link.wallOrder == null) link.wallOrder = counter++;
            lastId = link.wallOrder;
            link = links.get(link);
        }
        result.push(group);
    };
    return result;
}

/**
 * Centers the island relative to one of the walls in a room. The island wall
 * will be 96" from the target wall, which leaves 48" of clearance between the
 * island wall (with 24" cabinets) and the target wall (which also has 24"
 * cabinets). This attempts to shrink the island to as little as 48" to ensure
 * that the island has at least 48" of clearance on either end (if the target
 * wall has neighboring connected walls).
 *
 * Note that this procedure really only works for simple single-wall, L, and U
 * shaped layouts. More complicated layouts probably need the island to be
 * placed manually.
 */
export const alignIslandToWall = (island: Wall, target: Wall, context: Wall[]) => {
    const
        i = context.findIndex(x => x == target),
        hasPrev = i > 0 && isConnected(context[i-1], target),
        hasNext = i+1 < context.length && isConnected(target, context[i+1]),
        length = wallLength(target);

    let ilen = length;

    // We want to achieve 72" of clearance which is 24" of cabinets and 48" of
    // walking space.
    if(hasPrev) ilen -= 72;
    if(hasNext) ilen -= 72;

    // Maximum island size is 96" (eight feet) and minimum size is 48". Note
    // that this means that if the target wall has two connected neighbors, it
    // should be at least 48+72+72, or else this procedure will cut into the
    // 48" clerance needed to walk around the end of the island.
    ilen = Math.floor(3*clamp(ilen, 48, 96))/3;

    // Centered on the wall by default. The `o` variable represents the offset
    // from the end of the target wall.
    let o = (length - ilen) / 2;
    if(hasNext && hasPrev) {
        // leave it centered, but emit a warning if the wall is undersized for
        // this island. 48+72+72=192, the minimum target wall size when the
        // target wall has two neighbors.
        if(length < 192) {
            log.warn(`WallControllers.alignIslandToWall() target wall too short for island, not enough clearance on either end. target ID ${target.wallOrder}, island ID ${island.wallOrder}`);
        }
    }
    else if(hasNext) {
        // If it only has a wall after it, then make sure that the offset
        // clears the adjacent wall by at least 72". Note that bigger numbers
        // mean placement further to the left when facing the target wall
        // because the offset `o` is measured from the _end_ of the target
        // wall.
        o = Math.max(o, 72);
    }
    else if(hasPrev) {
        // Smaller means further to the right, so either take the centering
        // offset or the point that puts the island 72 inches away from the
        // preceding wall, whichever is further to the right.
        o = Math.min(o, length - ilen - 72);
    }

    const
        x1 = target.position.x + (length - o) * Math.cos(target.rotation.y),
        z1 = target.position.z + (length - o) * -Math.sin(target.rotation.y),

        x2 = x1 + 96 * Math.cos(target.rotation.y - Math.PI/2),
        z2 = z1 + 96 * -Math.sin(target.rotation.y - Math.PI/2);

    setWallLength(island, ilen);
    island.rotation.y = target.rotation.y - Math.PI;
    island.position.x = x2;
    island.position.z = z2;
}

/**
 * Finds the other side of an island or returns null if it doesn't exist. If
 * you don't pass an island, this will just be the neighboring wall, which
 * isn't that useful.
 */
export const getOtherSide = (island: Wall, walls: Wall[]) => {
    if(island.parentWallOrder < 1) {
        for(const wall of walls) {
            if(wall.parentWallOrder == island.wallOrder) {
                return wall;
            }
        }
        return null;
    } else {
        for(const wall of walls) {
            if(wall.wallOrder == island.parentWallOrder) {
                return wall;
            }
        }
        return null;
    }
}

export const normalizeAngle = (rad: number) => Math.atan2(Math.sin(rad), Math.cos(rad));

export const angleBetweenWalls = (prev: Wall, wall: Wall) => {
    let angle = -normalizeAngle(prev.rotation.y + Math.PI - wall.rotation.y);
    if(angle < 0) {
        angle += 2*Math.PI;
    }
    return angle;
};

export const angleBetweenDirections = (prev: number, wall: number) => {
    let angle = -normalizeAngle(prev + Math.PI - wall);
    if(angle < 0) {
        angle += 2*Math.PI;
    }
    return angle;
};
